import React, { useState, useRef } from 'react';
import { useDeepCompareEffect } from 'ahooks';
import { useVariantSelectionState } from '../context/VariantSelectionContext';
import type {
    LibraryImage,
    LibraryVideo,
    LibraryDiagram,
} from '../type/product-gallery-types';
import type Variant from '@modules/product/type/variant';
import { useLocation } from '@remix-run/react';

const getActiveIndex = (
    stack: Array<LibraryImage | LibraryVideo | LibraryDiagram>,
    selectedImageID?: number,
    previousImageID?: number
) => {
    let defaultIndex: number | null = null;

    if (selectedImageID && selectedImageID !== previousImageID) {
        defaultIndex =
            stack.findIndex((item) => item.ID === selectedImageID) ?? null;
    }

    return defaultIndex;
};

const useGalleryVariantSelect = (
    stack: Array<LibraryImage | LibraryVideo | LibraryDiagram>
) => {
    const { selectedVariant } = useVariantSelectionState();
    const prevRef = useRef<Variant | null>(null);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const initialSKU = searchParams.get('sku');

    const initialIndex =
        initialSKU && selectedVariant?.SKU === initialSKU
            ? getActiveIndex(
                  stack,
                  selectedVariant?.Image?.ID,
                  prevRef.current?.Image?.ID
              )
            : 0;

    const [slideIndex, setSlideIndex] = useState(initialIndex ?? 0);

    useDeepCompareEffect(() => {
        prevRef.current = null;
    }, [stack]);

    React.useEffect(() => {
        if (!selectedVariant) {
            return;
        }

        const prevVariant = prevRef.current;

        if (!prevVariant || prevVariant.ID === selectedVariant.ID) {
            if (!prevVariant) {
                prevRef.current = selectedVariant;
            }
            return;
        }

        const hasProductChanged =
            prevVariant.Product.ID !== selectedVariant.Product.ID;

        prevRef.current = selectedVariant;

        const activeIndex = hasProductChanged
            ? 0
            : getActiveIndex(
                  stack,
                  selectedVariant.Image?.ID,
                  prevVariant?.Image?.ID
              );

        if (activeIndex !== null) {
            setSlideIndex(activeIndex);
        }
    }, [selectedVariant, stack, prevRef]);

    return {
        slideIndex,
    };
};

export default useGalleryVariantSelect;
